import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { TextField, Button, CircularProgress } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDesktop, faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import type { IAlarmCentral, ICommunicationConfiguration } from "App/Modules/Models";
import { CommunicationInterfaceType, Protocol } from "App/Modules/Models";
import type { IAlarmCentralsRepository } from "App/Modules/Repositories";

import "./_edit-alarm-central.scss";

type EditAlarmCentralDialogProps = {
    alarmCentral: IAlarmCentral;
    alarmCentralRepository: IAlarmCentralsRepository;
    onClose: () => void;
    onEditAlarmCentralChange: () => void;
};

export const EditAlarmCentralDialog = (props: EditAlarmCentralDialogProps) => {
    const { t } = useTranslation();
    const { alarmCentral, alarmCentralRepository, onClose } = props;
    const [errors, setErrors] = useState([]);
    const [saveError, setSaveError] = useState(false);
    const [isSaved, setIsSaved] = useState(false);

    const [nameFormatIsValid, setNameFormatIsValid] = useState(true);
    const [HostNameFormatIsValid, setHostNameFormatIsValid] = useState(true);

    const [nameIsValid, setNameIsValid] = useState(true);
    const [hostNameIsValid, setHostNameIsValid] = useState(true);

    const [defaultAlarmCentralName, setDefaultAlarmCentralName] = useState("");
    const [defaultAlarmCentralHostName, setDefaultAlarmCentralHostName] = useState("");

    const validateAlarmCentralName = (alarmCentralName: string) => {
        if (!alarmCentralName) {
            return false;
        }
        return true;
    };

    const validateHostName = (hostName: string) => {
        if (!hostName) {
            return false;
        }
        return true;
    };

    const validateAlarmCentralNameFormat = (name: string) => {
        setNameFormatIsValid(true);
        const regex = new RegExp("^[a-zA-Z0-9_.]+( [a-zA-Z0-9_.]+)*$");

        if (!regex.test(name) && name !== "") {
            setNameFormatIsValid(false);
        }
    };

    const validateHostNameFormat = (hostName: string) => {
        setHostNameFormatIsValid(true);
        const regex = new RegExp("^[a-zA-Z0-9_./]+( [a-zA-Z0-9_./]+)*$");

        if (!regex.test(hostName) && hostName !== "") {
            setHostNameFormatIsValid(false);
        }
    };

    const validateForms = (alarmCentralObject) => {
        const validationErrors = [];
        const alarmCentralName = alarmCentralObject.name;
        const alarmCentralHostName = alarmCentralObject.communicationConfiguration.ip.externalAddress;

        if (!validateAlarmCentralName(alarmCentralName)) {
            validationErrors.push("alarmCentralName");
        }

        if (!validateHostName(alarmCentralHostName)) {
            validationErrors.push("hostName");
        }

        setErrors(validationErrors);
        return validationErrors.length === 0;
    };

    const handleClose = () => {
        onClose();
    };

    const handleSave = (event: React.MouseEvent) => {
        event.preventDefault();

        const editedAlarmCentral = {
            name: defaultAlarmCentralName,
            communicationConfiguration: {
                ip: {
                    ...alarmCentral.communicationConfiguration?.ip,
                    externalAddress: defaultAlarmCentralHostName,
                },
            },
        };

        const isValid = validateForms(editedAlarmCentral);

        if (isValid) {
            setIsSaved(true);
            alarmCentralRepository.patchAlarmCentral(alarmCentral.id, editedAlarmCentral as IAlarmCentral).subscribe(
                (response) => {
                    if (!response.hasError && response.data) {
                        props.onEditAlarmCentralChange();
                        onClose();
                        setIsSaved(false);
                    }
                },
                () => {
                    setSaveError(true);
                    setIsSaved(false);
                },
            );
        }
    };

    useEffect(() => {
        if (alarmCentral !== null) {
            setDefaultAlarmCentralName(alarmCentral.name);
        }
        if (alarmCentral.communicationConfiguration !== null) {
            setDefaultAlarmCentralHostName(alarmCentral.communicationConfiguration.ip.externalAddress);
        } else {
            alarmCentral.communicationConfiguration = {
                ip: {
                    externalPort: 443,
                    protocol: Protocol[Protocol.HTTPS],
                },
                type: CommunicationInterfaceType.WiredIP,
                typeName: "WiredIP",
            } as ICommunicationConfiguration;
        }
    }, [alarmCentral]);

    return (
        <div className="dialog card edit-alarm-central">
            <div className="dialog__section dialog__section--full">
                <h1 className="title title--h1">
                    <FontAwesomeIcon icon={faDesktop} fixedWidth />
                    {t("Entities.AlarmCentral.EditInformation")}
                </h1>
            </div>
            <div className="dialog__section dialog__section--full">
                <form className="form">
                    <div className="form__row">
                        <label htmlFor="alarm-central-name" className="caption">
                            {t("Entities.AlarmCentral.AlarmCentralName")}
                        </label>
                        <TextField
                            id="alarm-central-name"
                            fullWidth
                            required
                            size="small"
                            variant="outlined"
                            autoComplete="asdf"
                            value={defaultAlarmCentralName}
                            onChange={(e) => {
                                setDefaultAlarmCentralName(e.target.value);

                                let validationErrors: string[] = [...errors].filter(
                                    (error) => error !== "alarmCentralName",
                                );
                                if (
                                    !validateAlarmCentralName(e.target.value) &&
                                    validationErrors.indexOf("alarmCentralName") === -1
                                ) {
                                    setNameIsValid(false);
                                    validationErrors.push("alarmCentralName");
                                } else {
                                    setNameIsValid(true);
                                    validationErrors = validationErrors.filter((error) => error !== "alarmCentralName");
                                }
                                setErrors(validationErrors);

                                validateAlarmCentralNameFormat(e.target.value);
                            }}
                            helperText={
                                errors.includes("alarmCentralName")
                                    ? t("Validation.IsRequired", {
                                          propertyName: t("Entities.AlarmCentral.AlarmCentralName"),
                                      })
                                    : null
                            }
                            error={errors.includes("alarmCentralName")}
                        />
                        {!nameFormatIsValid ? (
                            <div className="edit-alarm-central__form-warning">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                <p className="caption">
                                    {t("Validation.MightNotBeValid", {
                                        propertyName: t("Entities.AlarmCentral.AlarmCentralName"),
                                    })}
                                </p>
                            </div>
                        ) : null}
                    </div>
                    <div className="form__row">
                        <label htmlFor="default-host-name" className="caption">
                            {t("Entities.AlarmCentral.HostnameAddress")}
                        </label>
                        <div className="emphasis-input">
                            <span className="emphasis-input__emph emphasis">https://</span>
                            <TextField
                                id="default-host-name"
                                fullWidth
                                required
                                size="small"
                                variant="outlined"
                                autoComplete="asdf"
                                value={defaultAlarmCentralHostName}
                                onChange={(e) => {
                                    setDefaultAlarmCentralHostName(e.target.value);

                                    let validationErrors: string[] = [...errors].filter(
                                        (error) => error !== "hostName",
                                    );
                                    if (
                                        !validateHostName(e.target.value) &&
                                        validationErrors.indexOf("hostName") === -1
                                    ) {
                                        setHostNameIsValid(false);
                                        validationErrors.push("hostName");
                                    } else {
                                        setHostNameIsValid(true);
                                        validationErrors = validationErrors.filter((error) => error !== "hostName");
                                    }
                                    setErrors(validationErrors);

                                    validateHostNameFormat(e.target.value);
                                }}
                                error={errors.includes("hostName")}
                            />
                            <span className="emphasis-input__emph emphasis">:443</span>
                        </div>
                        {errors?.includes("hostName") ? (
                            <p className="emphasis-input__error emphasis">
                                {t("Validation.IsRequired", {
                                    propertyName: t("Entities.AlarmCentral.HostnameAddress"),
                                })}
                            </p>
                        ) : null}
                        {!HostNameFormatIsValid ? (
                            <div className="edit-alarm-central__form-warning">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                <p className="caption">
                                    {t("Validation.MightNotBeValid", {
                                        propertyName: t("Entities.AlarmCentral.HostnameAddress"),
                                    })}
                                </p>
                            </div>
                        ) : null}
                    </div>
                </form>
            </div>
            {saveError ? (
                <div className="dialog__section dialog__section--full">
                    <div className="notice notice--critical">
                        <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                        <p className="caption">{t("Error.ErrorSaving")}</p>
                    </div>
                </div>
            ) : null}
            <div className="dialog__section--buttons">
                <Button onClick={handleClose}>{t("Gui.Buttons.Cancel")}</Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={isSaved || !nameIsValid || !hostNameIsValid}
                    startIcon={isSaved ? <CircularProgress size={20} color="inherit" /> : null}
                    onClick={(e: React.MouseEvent) => handleSave(e)}
                >
                    {t("Gui.Buttons.Save")}
                </Button>
            </div>
        </div>
    );
};
